import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
dayjs.extend(duration);

/** Convert input seconds into user-readable string with either:
 * a) integer seconds (0-60 seconds),
 * b) integer minutes (1-60/120 minutes),
 * c) hours with one decimal point of precision (> 1.0/2.0 hrs) */
export function secondsToShortString(
  sec: number | string | null | undefined,
  useIfNullOrUndefined = ''
): string {
  if (typeof sec === 'number') {
    const min = Math.floor(sec / 60);
    const hours = Math.floor(min / 60);
    const hoursOneDecimalPoint = Math.floor(min / 6) / 10;
    if (hours > 0) return `${hoursOneDecimalPoint} hours`;
    if (min > 0) return `${min} min`;
    return `${sec} sec`;
  }
  return sec ?? useIfNullOrUndefined;
}

/** Convert input seconds into user-readable string with:
 *  hours with two decimal points of precision (i.e. '0.03 hours' or '3.62 hours') */
export function secondsToHoursTwoDecimals(
  sec: number | string | null | undefined,
  useIfNullOrUndefined = ''
): string {
  if (typeof sec === 'number') {
    const min = Math.floor(sec / 60);
    const hoursTwoDecimalPoints = Math.floor(min / 0.6) / 100;
    return `${hoursTwoDecimalPoints} hours`;
  }
  return sec ?? useIfNullOrUndefined;
}

/** Convert microseconds to human-readable time in "hh:mm:ss.ms" format. Minutes always included, even if "00:" */
export function microsecToTime(
  microsec: number,
  roundDir: 'up' | 'down' | 'none'
): string {
  const signStr = microsec < 0 ? '-' : '';
  microsec = Math.abs(microsec);

  // Round microseconds to 1 digit only
  if (roundDir !== 'none')
    microsec =
      roundDir === 'up'
        ? Math.ceil(microsec / 100_000) * 100_000
        : Math.floor(microsec / 100_000) * 100_000;

  const sec = microsec / 1_000_000;
  const seconds = Math.floor(sec % 60);
  const minutes = Math.floor((sec / 60) % 60);
  const hours = Math.floor((sec / (60 * 60)) % 24);

  const hoursStr = hours > 0 ? padDigit(hours) + ':' : '';
  const minutesStr = minutes > 0 ? padDigit(minutes) + ':' : '00:';
  const secondsStr = padDigit(seconds) + '.';

  const microseconds = Math.floor(microsec % 1_000_000);
  const microsecStr =
    roundDir !== 'none' ? microseconds / 100_000 : microseconds;

  return signStr + hoursStr + minutesStr + secondsStr + microsecStr;
}

function padDigit(digit: number): string {
  return digit < 10 ? '0' + digit : '' + digit;
}

export const formatDuration = (milliseconds: number): string => {
  const duration = dayjs.duration(milliseconds);
  return duration.format('mm:ss');
};
